/**
 * Application root component that initializes routing, tRPC client,
 * React Query provider, and authentication state management.
 */
import { QueryClientProvider } from "@tanstack/react-query";
import { type TRPCLink, httpBatchLink } from "@trpc/client";
import { type AnyRouter } from "@trpc/server";
import { type Observer, observable } from "@trpc/server/observable";
import { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import superjson from "superjson";

import { AppShell } from "@/components/layout/AppShell";
import { useIsAccountAdmin } from "@/hooks/usePermission";
import { useSwitchScope } from "@/hooks/useSwitchScope";
import { classifyBootstrap, type BootstrapRefreshOutcome } from "@/lib/auth-bootstrap";
import { handleAuthFailure, refreshAccessToken } from "@/lib/auth-refresh";
import { queryClient } from "@/lib/query-client";
import { trpc } from "@/lib/trpc";
import { AdminPage } from "@/pages/admin";
import { ModuleEditorPage } from "@/pages/admin/ModuleEditorPage";
import { AgentPage } from "@/pages/agent";
import { Callback } from "@/pages/auth/Callback";
import { InvitePage } from "@/pages/auth/Invite";
import { Login } from "@/pages/auth/Login";
import { ConnectionsPage } from "@/pages/ConnectionsPage";
import { Dashboard } from "@/pages/Dashboard";
import { DefaultLanding } from "@/pages/DefaultLanding";
import { ModulePage } from "@/pages/ModulePage";
import { AccountSettingsPage } from "@/pages/settings/AccountSettings";
import { ModuleManagementPage } from "@/pages/settings/ModuleManagement";
import { useAuthStore } from "@/stores/auth";

// ════════════════════════════════════════════════════════════════════════════
// AUTH REFRESH INTERCEPTOR LINK
// ════════════════════════════════════════════════════════════════════════════

/**
 * tRPC link that transparently refreshes the access token on UNAUTHORIZED errors.
 *
 * When a tRPC call fails with UNAUTHORIZED, this link calls POST /auth/refresh.
 * On success, the original operation is retried (new cookie is automatically sent).
 * On failure, handleAuthFailure() clears auth state and redirects to /login.
 * A shared Promise singleton coalesces concurrent refresh attempts.
 */
function createAuthRefreshLink<TRouter extends AnyRouter>(): TRPCLink<TRouter> {
	return () => ({ op, next }) =>
		observable((observer: Observer<unknown, unknown>) => {
			// Only intercept queries and mutations (not subscriptions)
			const subscription = next(op).subscribe({
				next(value) {
					observer.next(value);
				},
				error(err) {
					const isUnauthorized =
						err instanceof Error &&
						"data" in err &&
						(err as { data?: { code?: string } }).data?.code === "UNAUTHORIZED";

					if (!isUnauthorized || op.type === "subscription") {
						observer.error(err);
						return;
					}

					// Attempt token refresh, then branch on the tri-state outcome.
					void refreshAccessToken().then((outcome) => {
						if (outcome === "terminal") {
							// Refresh token is expired/revoked — clear auth and redirect.
							handleAuthFailure();
							observer.error(err);
							return;
						}

						if (outcome === "transient") {
							// Recoverable failure (network/5xx) — surface the error WITHOUT
							// logging out; the session is still valid and a later request
							// (or the next refresh) can complete once the API recovers.
							observer.error(err);
							return;
						}

						// Refresh succeeded — retry the original operation once.
						next(op).subscribe({
							next(retryValue) {
								observer.next(retryValue);
							},
							error(retryErr) {
								// Retry also failed (e.g. new token invalid) — don't loop
								observer.error(retryErr);
							},
							complete() {
								observer.complete();
							},
						});
					});
				},
				complete() {
					observer.complete();
				},
			});

			return subscription;
		});
}

/**
 * Auth initializer component
 * Attempts to restore session from cookies on app load
 * @param props - component props
 * @param props.children - child components to render
 */
function AuthInitializer({ children }: { children: React.ReactNode }) {
	const location = useLocation();
	const { session, setSession, setUser, setTeams, setAccounts, setActiveTeam, setPermissions } = useAuthStore();
	const switchScope = useSwitchScope();
	const [isInitializing, setIsInitializing] = useState(true);
	// Result of the one-shot cold-load refresh: `null` until getSession first
	// returns no session and we attempt a refresh. An access token expiring while
	// a valid refresh token remains is the steady state, so we refresh before ever
	// treating a cold load as logged out.
	const [bootstrapRefresh, setBootstrapRefresh] = useState<BootstrapRefreshOutcome>(null);

	// Skip initialization for auth callback and invite routes (they handle their own auth)
	const isAuthRoute = location.pathname === "/auth/callback" || location.pathname === "/invite";

	// Try to get session from cookies
	const sessionQuery = trpc.auth.getSession.useQuery(undefined, {
		enabled: !session && !isAuthRoute,
		retry: 3, // bounded retry so a transient bootstrap blip recovers instead of forcing logout
		staleTime: Infinity, // Don't refetch on window focus
	});

	// Fetch user profile if session exists
	const meQuery = trpc.auth.me.useQuery(undefined, {
		enabled: !!sessionQuery.data && !session && !isAuthRoute,
		retry: 3, // bounded retry so a transient bootstrap blip recovers instead of forcing logout
		staleTime: Infinity,
	});

	useEffect(() => {
		// Skip for callback route
		if (isAuthRoute) {
			setIsInitializing(false);
			return;
		}

		// Already have session in store
		if (session) {
			setIsInitializing(false);
			return;
		}

		// Classify the bootstrap query state. Only a genuine `data === null` routes to
		// /login; a getSession/me error is transient (hold and let the query retry), so
		// a network/5xx blip on app load never logs out a validly-cookied user.
		const outcome = classifyBootstrap({
			sessionData: sessionQuery.data,
			sessionError: sessionQuery.error !== null,
			meData: meQuery.data,
			meError: meQuery.error !== null,
			refreshOutcome: bootstrapRefresh,
		});

		// getSession found no session but no refresh has been tried yet: attempt one
		// refresh (guarded so it fires once) before concluding the user is logged out.
		// A success refetches getSession; a terminal/transient result flows back
		// through classifyBootstrap on the next render.
		if (outcome === "needs-refresh") {
			if (bootstrapRefresh === null) {
				void refreshAccessToken().then((result) => {
					setBootstrapRefresh(result);
					if (result === "success") {
						void sessionQuery.refetch();
					}
				});
			}
			return;
		}

		if (outcome === "no-session") {
			setIsInitializing(false);
			return;
		}

		// transient or loading — hold initialization (stay on the spinner) and let the
		// retrying queries recover; never drop to an unauthenticated redirect here.
		if (outcome !== "ready") {
			return;
		}

		// ready — both session and profile are present (guard also narrows the types).
		if (!sessionQuery.data || !meQuery.data) {
			return;
		}
		setSession(sessionQuery.data);

		// API returns brands but we use teams internally
		const { brands: teams, accounts, permissions, ...user } = meQuery.data;
		setUser(user);
		setPermissions(permissions);

		setTeams(teams);
		setAccounts(accounts);

		// Restore active team from persisted storage or use first team
		const storedActiveTeam = useAuthStore.getState().activeTeam;
		const hasSavedTeam = storedActiveTeam && teams.some(t => t.id === storedActiveTeam.id);
		if (!hasSavedTeam && teams.length > 0 && teams[0]) {
			setActiveTeam(teams[0]);
		}

		setIsInitializing(false);
	}, [
		session,
		sessionQuery.data,
		sessionQuery.error,
		sessionQuery,
		meQuery.data,
		meQuery.error,
		isAuthRoute,
		bootstrapRefresh,
		setSession,
		setUser,
		setTeams,
		setAccounts,
		setActiveTeam,
		setPermissions,
	]);

	// Reconcile server scope with the restored/selected active team. The JWT
	// issued at login carries a default scope; a returning user's persisted
	// team may differ, so re-issue via switchScope so RPCs run under the team
	// shown in the UI (scope is no longer sent via headers).
	useEffect(() => {
		if (!session) return;
		const active = useAuthStore.getState().activeTeam;
		if (!active) return;
		if (active.id !== session.teamId || active.account.id !== session.accountId) {
			// On failure (e.g. membership revoked) the selector will correct the UI
			void switchScope(active).catch(() => undefined);
		}
	}, [session, switchScope]);

	// Show loading state while initializing (except for callback route)
	if (isInitializing && !isAuthRoute) {
		return (
			<div className="flex min-h-screen items-center justify-center bg-background">
				<div className="flex flex-col items-center space-y-4">
					<div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
					<p className="text-sm text-muted-foreground">Loading...</p>
				</div>
			</div>
		);
	}

	return <>{children}</>;
}

/**
 * Protected route wrapper
 * Redirects to login if not authenticated
 * @param props - component props
 * @param props.children - child components to render when authenticated
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
	const { session } = useAuthStore();

	if (!session) {
		return <Navigate to="/login" replace />;
	}

	return <>{children}</>;
}

/**
 * Super admin route wrapper
 * Redirects non-super-admin users to the default landing
 * @param props - component props
 * @param props.children - child components to render when authorized
 */
function SuperAdminRoute({ children }: { children: React.ReactNode }) {
	const { session, user } = useAuthStore();

	if (!session) {
		return <Navigate to="/login" replace />;
	}

	if (user?.role !== "super_admin") {
		return <Navigate to="/" replace />;
	}

	return <>{children}</>;
}

/**
 * Account admin route wrapper
 * Redirects users without account admin permissions to the default landing
 * @param props - component props
 * @param props.children - child components to render when authorized
 */
function AccountAdminRoute({ children }: { children: React.ReactNode }) {
	const { session } = useAuthStore();
	const isAccountAdmin = useIsAccountAdmin();

	if (!session) {
		return <Navigate to="/login" replace />;
	}

	if (!isAccountAdmin) {
		return <Navigate to="/" replace />;
	}

	return <>{children}</>;
}

/**
 * Main application component
 */
export function App() {
	const [trpcClient] = useState(() =>
		trpc.createClient({
			links: [
				// Auth refresh interceptor must come before httpBatchLink so it can
				// catch UNAUTHORIZED errors and transparently retry after token refresh
				createAuthRefreshLink(),
				httpBatchLink({
					url: `${import.meta.env.VITE_API_URL ?? ""}/trpc`,
					transformer: superjson,
					// Active tenant scope is carried by the signed session cookie and
					// changed via auth.switchScope — never declared by request headers.
					fetch(url, options) {
						return fetch(url, {
							...options,
							credentials: "include",
						});
					},
				}),
			],
		})
	);

	return (
		<trpc.Provider client={trpcClient} queryClient={queryClient}>
			<QueryClientProvider client={queryClient}>
				<BrowserRouter>
					<AuthInitializer>
						<Routes>
							{/* Public routes */}
							<Route path="/login" element={<Login />} />
							<Route path="/auth/callback" element={<Callback />} />
							<Route path="/invite" element={<InvitePage />} />

							{/* Protected routes with app shell */}
							<Route
								element={
									<ProtectedRoute>
										<AppShell />
									</ProtectedRoute>
								}
							>
								<Route path="/dashboard" element={<Dashboard />} />
								<Route path="/agent" element={<AgentPage />} />
								<Route
									path="/admin"
									element={
										<SuperAdminRoute>
											<AdminPage />
										</SuperAdminRoute>
									}
								/>
								<Route
									path="/admin/modules/:moduleId"
									element={
										<SuperAdminRoute>
											<ModuleEditorPage />
										</SuperAdminRoute>
									}
								/>
								<Route
									path="/settings/account"
									element={
										<AccountAdminRoute>
											<AccountSettingsPage />
										</AccountAdminRoute>
									}
								/>
								<Route
									path="/settings/users"
									element={<Navigate to="/settings/account?tab=users" replace />}
								/>
								<Route
									path="/settings/modules"
									element={
										<AccountAdminRoute>
											<ModuleManagementPage />
										</AccountAdminRoute>
									}
								/>
								<Route path="/connections" element={<ConnectionsPage />} />
								{/* Dynamic module rendering — /* suffix allows custom interfaces to define nested routes */}
								<Route path="/m/:moduleSlug/*" element={<ModulePage />} />
								<Route path="/" element={<DefaultLanding />} />
							</Route>

							{/* Catch-all redirect */}
							<Route path="*" element={<Navigate to="/" replace />} />
						</Routes>
					</AuthInitializer>
				</BrowserRouter>
			</QueryClientProvider>
		</trpc.Provider>
	);
}
